import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import { useFormik } from 'formik';
import { useClient } from 'jsonapi-react';
import { FilterSelectNative } from '@/components/custom/filter';
import { ProjectsProvider } from '../provider';
import { useTranslation } from 'react-i18next';
import { useCourse } from '@/hooks/useCourse';
import { Header } from '@/components/custom/header/header';
import TabNavBar from '../TabNavBar';
import { FilterSelectMulti } from '@/components/custom/filter/filter-select-multi';
import usePermission from '@/hooks/usePermission';
import usePlayfulTeacher from '@/hooks/usePlayfulTeacher';
import { Loader } from '@/components/custom/loader';
import { EmptyState } from '@/components/custom/empty-state';
import GroupProjectCard from './GroupProjectCard';
import toast from 'react-hot-toast';
import { confirm } from '@/lib/confirm';
import useAuthorityLevel from '@/hooks/useAuthorityLevel';
import { Error404 } from '@/components/custom/error';
import Select from 'react-select';
import { Dashboard, DashboardContent } from '@/layouts/dashboard';
import { ProjectsPlayfulTeacherList } from '../PlayfulTeacher/ProjectsPlayfulTeacherList';

export function GroupProjects(props) {
  return (
    <ProjectsProvider>
      <ScreenContentGroupProjects {...props} />
    </ProjectsProvider>
  );
}

export function ScreenContentGroupProjects(props) {
  const { uri } = props;

  const { t } = useTranslation();
  const { courses, getSimpleCourses } = useCourse();
  const [classrooms, setClassrooms] = useState([]);
  const [groupedProjects, setGroupedProjects] = useState([]);
  const [filteredGroupedProjects, setFilteredGroupedProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const authorityLevel = useAuthorityLevel();
  const isPlayfulTeacherTheme = usePlayfulTeacher();
  const canViewProjects = usePermission('Visualizar lista de projetos', 'Projetos');

  const client = useClient();

  const filters = useFormik({
    initialValues: {
      course: 'blank',
      classroom: { value: 'blank', label: t('filter.blankLabelAll') },
      approvedProjects: false,
      projectsInProgress: false
    }
  });

  const getClassrooms = async () => {
    if (filters.values?.course && filters.values.course !== 'blank') {
      const { data } = await client.fetch(`classrooms?simple=true&filter[course_id]=${filters.values.course}`);
      setClassrooms(data);
    }
  };

  const getGroupedProjects = async () => {
    setLoading(true);
    const { data, error } = await client.fetch('project_groups?filter[project_model]=random');
    if (error) {
      toast.error('Erro ao buscar Grupo de Projetos');
    } else {
      setGroupedProjects(data);
      setLoading(false);
    }
  };

  const deleteGroupProject = async id => {
    setLoading(true);
    const { error } = await client.delete(['project_groups', id]);
    if (error) {
      toast.error(t('toast.errorDeleteProjectGroup'));
    } else {
      toast.success(t('toast.successDeleteProjectGroup'));
      setFilteredGroupedProjects(filteredGroupedProjects.filter(p => p.id !== id));
      setLoading(false);
    }
  };

  const handleDeleteGroupProject = async id => {
    const confirmed = await confirm({
      title: t('projects.warningDeleteProject'),
      confirmText: t('button.yes'),
      cancelText: t('button.no')
    });
    if (confirmed) {
      deleteGroupProject(id);
    }
  };

  useEffect(() => {
    getSimpleCourses();
    getGroupedProjects();
  }, []);

  useEffect(() => {
    getClassrooms();
    filters.setFieldValue('classroom', { value: 'blank', label: t('filter.blankLabelAll') });
  }, [filters.values.course]);

  useEffect(() => {
    let newGroupedProjects = groupedProjects;
    if (filters.values.course !== 'blank') {
      newGroupedProjects = groupedProjects.filter(u => u.projects.some(p => p['projectable-type'] === 'Course' && p['projectable-id'] === +filters.values.course));
    }
    if (filters.values.classroom.value !== 'blank') {
      newGroupedProjects = groupedProjects.filter(u => u.projects.some(p => p['projectable-type'] === 'Classroom' && p['projectable-id'] === +filters.values.classroom.value));
    }

    if (filters.values.approvedProjects === true) {
      newGroupedProjects = newGroupedProjects.filter(u => u.projects.every(p => p.approved === true));
    }
    if (filters.values.projectsInProgress === true) {
      newGroupedProjects = newGroupedProjects.filter(u => u.projects.some(p => p.approved === null));
    }
    setFilteredGroupedProjects(newGroupedProjects);
  }, [filters.values, groupedProjects]);

  const otherFilters = [
    {
      label: t('metrics.approvedProjects'),
      value: filters.values.approvedProjects,
      onChange: () => {
        filters.setFieldValue('projectsInProgress', false);
        filters.setFieldValue('approvedProjects', !filters.values.approvedProjects);
      }
    },
    {
      label: t('metrics.projectsInProgress'),
      value: filters.values.projectsInProgress,
      onChange: () => {
        filters.setFieldValue('approvedProjects', false);
        filters.setFieldValue('projectsInProgress', !filters.values.projectsInProgress);
      }
    }
  ];

  const breadcrumbs = {
    title: 'Atividades',
    nav: [
      {
        route: uri,
        name: 'Projetos',
        isActive: true
      }
    ]
  };

  if (authorityLevel !== 'student' && isPlayfulTeacherTheme) {
    return (
      <ProjectsProvider>
        {canViewProjects && (
          <ProjectsPlayfulTeacherList
            uri={uri}
            selectedTab="grupos"
            courses={courses}
            classrooms={classrooms}
            filters={filters}
            projects={filteredGroupedProjects}
            loading={loading}
            onDeleteProject={handleDeleteGroupProject}
          />
        )}
      </ProjectsProvider>
    );
  }

  return (
    <ProjectsProvider>
      {authorityLevel !== 'student' ? (
        <Dashboard>
          <Header />

          {canViewProjects && (
            <DashboardContent
              variant="plain"
              breadcrumbs={breadcrumbs}
            >
              <TabNavBar selectedTab="grupos" />

              <div className="filter-bar">
                <div className="filter-bar__inner">
                  <FilterSelectNative
                    blankLabel={t('filter.blankLabelAll')}
                    label={t('filter.labelSelectCourse')}
                    value={filters.values.course}
                    onChange={e => filters.setFieldValue('course', e.target.value)}
                    options={courses.map(o => ({
                      id: o.id,
                      name: o.title
                    }))}
                  />
                  <div>
                    <label className="form__label">{t('filter.labelSelectClassroom')}</label>
                    <Select
                      value={filters.values.classroom}
                      openMenuOnFocus
                      options={[
                        { value: 'blank', label: t('filter.blankLabelAll') },
                        ...classrooms?.map(item => {
                          return { value: item.id, label: item?.institution?.name + ' - ' + item?.course?.title + ' - ' + item.title };
                        })
                      ]}
                      className="react-multi-select filter-bar__multi-select"
                      classNamePrefix="react-multi-select"
                      placeholder={loading ? t('loader.loading2') : t('filter.blankLabelAll')}
                      noOptionsMessage={() => 'Sem opções'}
                      components={{
                        IndicatorSeparator: () => null,
                        ClearIndicator: () => null
                      }}
                      onChange={e => filters.setFieldValue('classroom', e)}
                    />
                  </div>
                  <FilterSelectMulti
                    data={otherFilters}
                    title="Status"
                  />
                </div>

                {usePermission('Criar projetos', 'Projetos') && (
                  <Link
                    to="/projetos/criar/passo/curso-turma"
                    className="btn btn--wide btn--primary"
                  >
                    {t('projects.newProjects')}
                  </Link>
                )}
              </div>

              {loading && <Loader />}

              {filteredGroupedProjects?.length === 0 && !loading && (
                <EmptyState
                  type="project"
                  title={t('emptyState.textData')}
                  text={t('emptyState.createNewProjectInteract')}
                />
              )}

              {!loading && (
                <div className="tab__cards">
                  {filteredGroupedProjects?.map(p => (
                    <GroupProjectCard
                      data={p}
                      id={p.id}
                      group={true}
                      individual={false}
                      personalized={false}
                      deleteGroupProject={deleteGroupProject}
                    />
                  ))}
                </div>
              )}
            </DashboardContent>
          )}
        </Dashboard>
      ) : (
        <Error404 />
      )}
    </ProjectsProvider>
  );
}
